import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  type AccountSession,
  type AccountSummary,
  type LockedAccount,
  type ProvisionalPassword,
  type VerificationMethod,
} from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * Every read of this feature is AUDITED and needs a stated reason (10 characters at least) typed by the person looking: the queries
 * stay disabled until they have, like every other audited read of this app.
 */
const reasonOk = (reason: string): boolean => reason.trim().length >= 10;

export function useLockedAccountsQuery(
  reason: string,
): UseQueryResult<{ accounts: readonly LockedAccount[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['access-locked', reason],
    queryFn: () =>
      apiRequest<{ accounts: readonly LockedAccount[] }>(
        `/admin/access/locked?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: accessToken !== null && reasonOk(reason),
  });
}

export function useProvisionalPasswordsQuery(
  reason: string,
): UseQueryResult<{ accounts: readonly ProvisionalPassword[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['access-provisional', reason],
    queryFn: () =>
      apiRequest<{ accounts: readonly ProvisionalPassword[] }>(
        `/admin/access/provisional?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: accessToken !== null && reasonOk(reason),
  });
}

/** `GET /admin/access/lookup` - one account by its address. A lookup, not a list: run on demand with the address the person typed. */
export function useLookupAccountMutation(): UseMutationResult<
  { account: AccountSummary | null },
  ApiError,
  { email: string; reason: string }
> {
  const { accessToken } = useSession();
  return useMutation({
    mutationFn: ({ email, reason }) =>
      apiRequest<{ account: AccountSummary | null }>(
        `/admin/access/lookup?${new URLSearchParams({ email, reason }).toString()}`,
        { accessToken },
      ),
  });
}

export function useAccountSessionsQuery(
  userId: string | null,
  reason: string,
): UseQueryResult<{ sessions: readonly AccountSession[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['access-sessions', userId, reason],
    queryFn: () =>
      apiRequest<{ sessions: readonly AccountSession[] }>(
        `/admin/accounts/${userId ?? ''}/sessions?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: accessToken !== null && userId !== null && reasonOk(reason),
  });
}

/** `POST /admin/accounts/:id/unlock`. The password travels in the body only and is not kept (`gcTime: 0`). */
export function useUnlockAccountMutation(): UseMutationResult<
  { status: string },
  ApiError,
  { userId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ userId, reason, confirmPassword }) =>
      apiRequest<{ status: string }>(`/admin/accounts/${userId}/unlock`, {
        method: 'POST',
        body: { reason, confirmPassword },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['access-locked'] });
    },
  });
}

/** `POST /admin/accounts/:id/sessions/revoke` - ends every session of another account. */
export function useRevokeSessionsMutation(): UseMutationResult<
  { status: string; sessionsEnded: number },
  ApiError,
  { userId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ userId, reason, confirmPassword }) =>
      apiRequest<{ status: string; sessionsEnded: number }>(
        `/admin/accounts/${userId}/sessions/revoke`,
        {
          method: 'POST',
          body: { reason, confirmPassword },
          accessToken,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['access-sessions'] });
    },
  });
}

/**
 * `POST /admin/platform-accounts/:id/temporary-password` - a new provisional password for a platform ADMINISTRATOR (the API refuses a
 * superadmin). The response carries the password: shown once by the caller, never cached (`gcTime: 0`).
 */
export function useResetAdminPasswordMutation(): UseMutationResult<
  { userId: string; temporaryPassword: string; expiresAt: string },
  ApiError,
  {
    userId: string;
    reason: string;
    verificationMethod: VerificationMethod;
    confirmPassword: string;
  }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ userId, reason, verificationMethod, confirmPassword }) =>
      apiRequest<{ userId: string; temporaryPassword: string; expiresAt: string }>(
        `/admin/platform-accounts/${userId}/temporary-password`,
        {
          method: 'POST',
          body: { reason, verificationMethod, confirmPassword },
          accessToken,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['access-provisional'] });
      void queryClient.invalidateQueries({ queryKey: ['access-sessions'] });
    },
  });
}
